import React, { useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { Checkbox } from './ui/checkbox';

const CATEGORIES = [
  'Apparel',
  'Electronics',
  'Drinkware',
  'Stationery',
  'Bags & Travel',
  'Food & Gourmet',
  'Wellness',
  'Desk Accessories',
  'Festive & Seasonal',
  'Luxury',
  'Eco-Friendly',
  'Custom/Other',
];

const PRICE_RANGES = [
  { label: 'Under ₹300', min: 0, max: 300 },
  { label: '₹300 - ₹700', min: 300, max: 700 },
  { label: '₹700 - ₹1500', min: 700, max: 1500 },
  { label: '₹1500 - ₹5000', min: 1500, max: 5000 },
  { label: 'Above ₹5000', min: 5000, max: null },
];

const STOCK_STATUS = ['In Stock', 'Available on Order', 'Limited Stock', 'Discontinued'];

export const SearchFilters = ({ onSearch, onFilterChange, filters }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onSearch(searchTerm);
  };

  const handleFilterChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    onFilterChange({});
    setSearchTerm('');
    onSearch('');
  };

  const activeFilterCount = Object.keys(filters).filter(key => filters[key]).length;

  return (
    <div className="bg-white border-b border-slate-200 p-4">
      <div className="max-w-7xl mx-auto flex gap-3">
        <form onSubmit={handleSearchSubmit} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              data-testid="search-input"
              type="text"
              placeholder="Search products by name, description, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 h-11 w-full"
            />
          </div>
        </form>

        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="gap-2 relative" data-testid="filters-btn">
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-consortium-green text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Filter Products</SheetTitle>
              <SheetDescription>Refine your product search</SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              <div>
                <Label className="mb-3 block">Category</Label>
                <Select value={filters.category || ''} onValueChange={(value) => handleFilterChange('category', value)}>
                  <SelectTrigger data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-3 block">Price Range</Label>
                <Select value={filters.priceRange || ''} onValueChange={(value) => handleFilterChange('priceRange', value)}>
                  <SelectTrigger data-testid="price-filter">
                    <SelectValue placeholder="All Prices" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Prices</SelectItem>
                    {PRICE_RANGES.map((range, idx) => (
                      <SelectItem key={idx} value={JSON.stringify(range)}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-3 block">Stock Status</Label>
                <Select value={filters.stock_status || ''} onValueChange={(value) => handleFilterChange('stock_status', value)}>
                  <SelectTrigger data-testid="stock-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {STOCK_STATUS.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-3 block">Sort By</Label>
                <Select value={filters.sort_by || 'created_at'} onValueChange={(value) => handleFilterChange('sort_by', value)}>
                  <SelectTrigger data-testid="sort-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">Newest Added</SelectItem>
                    <SelectItem value="recommended_selling_price">Price: Low to High</SelectItem>
                    <SelectItem value="view_count">Most Viewed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={clearFilters} variant="outline" className="w-full" data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-2" />
                Clear All Filters
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};
